import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('toggle', this.#onToggle, true);
    this.#setupAnimatedElementListeners();
    this.#syncMobileBottomPanelState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('toggle', this.#onToggle, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle clicks inside the drawer.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-mobile-bottom-trigger]');

    if (trigger instanceof HTMLElement) {
      event.preventDefault();

      const handle = trigger.dataset.mobileBottomTrigger;

      if (!handle) return;

      const nextHandle = trigger.getAttribute('aria-expanded') === 'true' ? null : handle;

      this.#setMobileBottomPanel(nextHandle);

      if (nextHandle) {
        this.#closeMobileTopLevelAccordions();
      }

      return;
    }

    this.#handleSamePageHashLinkClick(event);
  };

  /**
   * Keep the mobile footer panel and the top-level accordions mutually exclusive.
   * @param {Event} event
   */
  #onToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (!event.target.matches('.menu-drawer__top-level-accordion details')) return;
    if (!event.target.open) return;

    this.#setMobileBottomPanel(null);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.#restoreMobileInitialState();
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    return this.#close(this.refs.details);
  }

  /**
   * Close the drawer before following same-page hash links so scroll is no longer locked.
   * @param {MouseEvent} event
   */
  #handleSamePageHashLinkClick(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');

    if (!(link instanceof HTMLAnchorElement) || !this.refs.menuDrawer.contains(link)) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    const currentUrl = new URL(window.location.href);

    if (
      url.origin !== currentUrl.origin ||
      url.pathname !== currentUrl.pathname ||
      url.search !== currentUrl.search ||
      !url.hash
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.#closeImmediately(this.refs.details);
    window.requestAnimationFrame(() => {
      navigateToSamePageHash(url);
    });
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return Promise.resolve();

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    return onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          this.#restoreMobileBottomPanelDefault();
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Close a details drawer without waiting for the close animation.
   * Used before same-page hash navigation so the page can scroll immediately.
   *
   * @param {HTMLDetailsElement} details
   */
  #closeImmediately(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    reset(details);

    if (details === this.refs.details) {
      this.#restoreMobileBottomPanelDefault();
      removeTrapFocus();
      const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
      openDetails.forEach(reset);
      document.documentElement.removeAttribute('scroll-lock');
    } else {
      trapFocus(this.refs.details);
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Close all open top-level mobile accordions.
   */
  #closeMobileTopLevelAccordions() {
    const openTopLevelDetails = this.querySelectorAll('.menu-drawer__top-level-accordion details[open]');

    openTopLevelDetails.forEach((details) => {
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
    });
  }

  /**
   * Synchronize the footer trigger state with the currently visible mobile bottom panel.
   */
  #syncMobileBottomPanelState() {
    const defaultPanel = this.querySelector('[data-mobile-bottom-panel].is-active, [data-mobile-bottom-panel-default="true"]');
    const handle = defaultPanel instanceof HTMLElement ? defaultPanel.dataset.mobileBottomPanel ?? null : null;

    this.#setMobileBottomPanel(handle);
  }

  /**
   * Restore the mobile footer panel to its default state when the drawer closes.
   */
  #restoreMobileBottomPanelDefault() {
    const defaultPanel = this.querySelector('[data-mobile-bottom-panel-default="true"]');
    const handle = defaultPanel instanceof HTMLElement ? defaultPanel.dataset.mobileBottomPanel ?? null : null;

    this.#setMobileBottomPanel(handle);
  }

  /**
   * Reset the mobile drawer to its default state: first main accordion open, bottom panels closed.
   */
  #restoreMobileInitialState() {
    this.#closeMobileTopLevelAccordions();
    this.#restoreMobileBottomPanelDefault();

    const defaultAccordion = this.querySelector('accordion-custom.menu-drawer__top-level-accordion[open-by-default-on-mobile] details');

    if (defaultAccordion instanceof HTMLDetailsElement) {
      defaultAccordion.open = true;
    }
  }

  /**
   * Show one mobile footer panel and hide the rest.
   * @param {string | null} handle
   */
  #setMobileBottomPanel(handle) {
    const shell = this.querySelector('.menu-drawer__mobile-shell');
    const triggers = this.querySelectorAll('[data-mobile-bottom-trigger]');
    const panels = this.querySelectorAll('[data-mobile-bottom-panel]');

    let hasActivePanel = false;

    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      const isActive = handle !== null && trigger.dataset.mobileBottomTrigger === handle;

      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      trigger.classList.toggle('menu-drawer__mobile-utility-link--active', isActive);
    });

    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const isActive = handle !== null && panel.dataset.mobileBottomPanel === handle;

      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);

      if (isActive) {
        hasActivePanel = true;
      }
    });

    if (shell instanceof HTMLElement) {
      shell.toggleAttribute('data-mobile-bottom-panel-open', hasActivePanel);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * Update the URL hash and scroll after the drawer has released scroll lock.
 * @param {URL} url
 */
function navigateToSamePageHash(url) {
  const targetId = decodeHashValue(url.hash);
  if (!targetId) return;

  const oldURL = window.location.href;

  if (url.href !== oldURL) {
    history.pushState(window.history.state, '', url);
    window.dispatchEvent(
      new HashChangeEvent('hashchange', {
        oldURL,
        newURL: url.href,
      })
    );
  }

  const target = findHashTarget(targetId);

  if (!target) return;

  const shouldReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  target.scrollIntoView({
    behavior: shouldReduceMotion ? 'auto' : 'smooth',
    block: 'start',
  });
}

/**
 * Decode a URL hash for matching DOM IDs.
 * @param {string} hash
 * @returns {string}
 */
function decodeHashValue(hash) {
  const value = String(hash).replace(/^#/, '').trim();
  if (!value) return '';

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Find an anchor target by id or legacy name.
 * @param {string} targetId
 * @returns {Element | null}
 */
function findHashTarget(targetId) {
  return document.getElementById(targetId) ?? document.getElementsByName(targetId)[0] ?? null;
}
